import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Search, 
  Bell, 
  Building2, 
  User,
  Menu,
  Keyboard,
  ChevronRight,
  ChevronLeft,
  Sparkles
} from 'lucide-react';

const OnboardingContext = createContext({
  startTour: () => {},
  resetTour: () => {},
});

export function useOnboarding() {
  return useContext(OnboardingContext);
}

// Tour steps
const TOUR_STEPS = [
  {
    title: 'Bem-vindo à OFS!',
    description: 'Vamos fazer um tour rápido pelas principais funcionalidades do sistema.',
    icon: Sparkles,
  },
  {
    title: 'Menu de Navegação',
    description: 'Use o menu lateral para acessar todos os módulos: Financeiro, Operacional, Cadastros e Relatórios.',
    icon: Menu,
  },
  {
    title: 'Busca Global',
    description: 'Pressione Ctrl+K para buscar qualquer registro rapidamente: contas, clientes, contratos...',
    icon: Search,
  },
  {
    title: 'Notificações',
    description: 'Receba alertas de contas vencidas e avisos importantes no ícone de sino.',
    icon: Bell,
  },
  {
    title: 'Seletor de Empresa',
    description: 'Se você tem múltiplas empresas, alterne entre elas facilmente.',
    icon: Building2,
  },
  {
    title: 'Atalhos de Teclado',
    description: 'Pressione ? para ver todos os atalhos disponíveis e aumentar sua produtividade.',
    icon: Keyboard,
  },
  {
    title: 'Pronto para começar!',
    description: 'Explore o sistema à vontade. Estamos aqui para ajudar!',
    icon: Sparkles,
  },
];

export function OnboardingProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (!user?.id || location.pathname !== '/dashboard') return;
    
    const tourKey = `axion_tour_completed_${user.id}`;
    const hasSeenTour = localStorage.getItem(tourKey);
    
    if (hasSeenTour === 'true') {
      setIsOpen(false);
      return;
    }
    
    const timer = setTimeout(() => setIsOpen(true), 1500);
    return () => clearTimeout(timer);
  }, [user?.id, location.pathname]);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsOpen(true);
  }, []);

  const resetTour = useCallback(() => {
    if (user) {
      localStorage.removeItem(`axion_tour_completed_${user.id}`);
      toast.success('Tour resetado! Recarregue a página para ver novamente.');
    }
  }, [user]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    setIsOpen(false);
    if (user) {
      localStorage.setItem(`axion_tour_completed_${user.id}`, 'true');
    }
    toast.success('Tour concluído! Explore o sistema à vontade.');
  };

  const handleSkip = () => {
    setIsOpen(false);
    if (user) {
      localStorage.setItem(`axion_tour_completed_${user.id}`, 'true');
    }
  };

  const step = TOUR_STEPS[currentStep];
  const Icon = step?.icon || Sparkles;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  return (
    <OnboardingContext.Provider value={{ startTour, resetTour }}>
      {children}
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleSkip(); }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            {/* O botao X de fechar ja vem embutido no DialogContent (ui/dialog).
                Nao adicionar outro aqui — senao aparecem dois X (bug corrigido). */}
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <DialogTitle>{step?.title}</DialogTitle>
            </div>
          </DialogHeader>
          
          <div className="py-4">
            <DialogDescription className="text-base">
              {step?.description}
            </DialogDescription>
          </div>

          <div className="space-y-4">
            <Progress value={progress} className="h-1" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {currentStep + 1} de {TOUR_STEPS.length}
              </span>
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button variant="outline" size="sm" onClick={handlePrev}>
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Anterior
                  </Button>
                )}
                <Button size="sm" onClick={handleNext}>
                  {currentStep === TOUR_STEPS.length - 1 ? 'Concluir' : 'Próximo'}
                  {currentStep < TOUR_STEPS.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </OnboardingContext.Provider>
  );
}
